import React from 'react';
import NProgress from 'nprogress';
import { useRouter } from 'next/router';
import { Card, Input } from '@avila-tek/ui/src';
import { useMutation } from '@apollo/client';
import { validateString } from 'avilatek-utils';
import { CREATE_CATEGORY, UPDATE_CATEGORY } from '../../graphql/mutation';
import { Category } from '../../models';
import { FormHeader, FormButtons } from '../shared/Forms';
import { useNotify } from '../../hooks';

interface CategoryFormProps {
  category?: Category;
}

function CategoryForm({ category }: CategoryFormProps) {
  const notify = useNotify();
  const router = useRouter();
  const [name, setName] = React.useState(category?.name ?? '');
  const [disabled, setDisabled] = React.useState(false);

  const [createCategoryMutation] = useMutation<{
    createCategory: {
      category: Category;
    };
  }>(CREATE_CATEGORY);

  const [updateCategoryMutation] = useMutation<{
    updateCategory: {
      recordId: string;
    };
  }>(UPDATE_CATEGORY);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (disabled) return;
    try {
      NProgress.start();
      setDisabled(true);
      if (!validateString(name) || name === '') {
        return notify('El nombre es inválido', 'error');
      }
      const categoryData = {
        name,
      };
      if (!category?._id) {
        createCategory(categoryData);
      } else {
        updateCategory(categoryData);
      }
    } catch (error) {
      console.log(error);
      notify(error, 'error');
    } finally {
      NProgress.done();
      setDisabled(false);
    }
  };

  const createCategory = async (categoryData: Partial<Category>) => {
    try {
      const { data } = await createCategoryMutation({
        variables: {
          data: {
            ...categoryData,
          },
        },
      });
      if (data?.createCategory?.category?._id) {
        notify('Categoría se ha creado con éxito', 'success');
        router.push('/app/maintenance/categories');
      } else {
        notify('Ha ocurrido un error al crear la categoría', 'error');
      }
    } catch (error) {
      return notify(
        `Ha ocurrido un error al crear la categoría: ${error.message}`,
        'error'
      );
    }
  };

  const updateCategory = async (categoryData: Partial<Category>) => {
    try {
      const { data } = await updateCategoryMutation({
        variables: {
          record: {
            ...categoryData,
          },
          filter: {
            _id: category._id,
          },
        },
      });
      if (data?.updateCategory?.recordId) {
        notify('Categoría actualizada con éxito', 'success');
        router.push('/app/maintenance/categories');
      } else {
        notify('Ha ocurrido un error al actualizar la categoría', 'error');
      }
    } catch (error) {
      return notify(
        `Ha ocurrido un error al crear la categoría: ${error.message}`,
        'error'
      );
    }
  };

  const toggleActive = () => updateCategory({ active: !category.active });

  return (
    <form
      method="POST"
      onSubmit={onSubmit}
      className="flex flex-col w-full space-y-10"
    >
      <Card>
        <FormHeader
          href="/app/maintenance/categories"
          title={category ? 'Actualizar categoría' : 'Crear categoría'}
          backBtnText="Categorías"
        />
        <Card.Body>
          <div className="w-full md:w-1/2 flex ">
            <div className="w-full">
              <Input
                value={name}
                label="Nombre"
                type="text"
                placeholder="Ingrese un nombre"
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
          </div>
        </Card.Body>
      </Card>
      <FormButtons
        type={category ? 'update' : 'create'}
        toggleActive={toggleActive}
        active={category?.active}
        disabled={disabled}
        onSubmit={onSubmit}
      />
    </form>
  );
}

export default CategoryForm;
